import { Component, createEffect, onCleanup } from 'solid-js';
import { unwrap } from 'solid-js/store';
import { createQuery } from '@tanstack/solid-query';

import { appConfigQuery, useChannel } from '~/store/tanstack-query';
import { updateChannelMetadata, updateSchedulesMetadata } from '~/cast/VrioOtt';

import { addGoLiveListener, getLiveEdgeTimestamp } from '~/cast/lib';
import { isProgramCompletionActive } from '~/utils/env';
import { CreateGetStreamedTime } from '~/utils/types';
import { useSchedules } from './useSchedules';

interface LiveMetadataUpdaterProps {
  channelId: string;
  start?: number;
  streamedContentTime: CreateGetStreamedTime;
  onFinished: () => void;
  onProgramCompletion: () => void;
}

export const LiveMetadataUpdater: Component<LiveMetadataUpdaterProps> = (
  props,
) => {
  const appConfig = createQuery(() => appConfigQuery());
  const channelId = () => props.channelId;
  const [schedulesState, updateSchedulesState] = useSchedules(
    channelId,
    () => appConfig.data?.metadata.live,
    () => props.start,
  );
  const { channel, isPending: isPendingChannel } = useChannel(channelId);

  const handleGoLive = () => {
    updateSchedulesState({
      currentTimestamp: getLiveEdgeTimestamp(),
      useNowNext: true,
      hardUpdate: true,
    })?.catch((error) => {
      console.error(
        '[LiveMetadataUpdater>handleGoLive] error on updateSchedulesState',
        error,
      );
    });
  };

  const removeGoLiveListener = addGoLiveListener(handleGoLive);
  onCleanup(removeGoLiveListener);

  createEffect((prevIsLoadingMetadata) => {
    const isLoadingMetadata = schedulesState.isLoading || isPendingChannel();

    if (prevIsLoadingMetadata && !isLoadingMetadata) {
      props.onFinished();

      if (isProgramCompletionActive) {
        props.onProgramCompletion();
      }

      props.streamedContentTime?.reset();
      props.streamedContentTime?.start();
    }

    return isLoadingMetadata;
  });

  createEffect(() => {
    const currentChannel = channel();
    if (currentChannel) {
      updateChannelMetadata(currentChannel);
    }
  });

  createEffect((prev) => {
    const currentSchedules = schedulesState.schedules;
    if (!currentSchedules.current || prev === currentSchedules.current)
      return currentSchedules.current;

    updateSchedulesMetadata(unwrap(currentSchedules));
    return currentSchedules.current;
  });

  return undefined;
};
